'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { amplitudeSendEvent, getScreenName } from '@musinsa/frontend-utils';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { curatorQueries } from '../query-factory';
import { CreateGoodsCurationForm } from './CreateGoodsCurationForm';
import { SelectGoodsSelectionStep } from './SelectGoodsSelectionStep';
import { useShopEditContext } from './ShopEditContext';
import type { ShopCurationEditFormData } from './ShopGoodsCurationEdit';
import { type CurationFormGoodsItem, editCurationFormSchema } from './type';
import { getCurationFormDraftKey, useCurationFormDraft } from './useCurationFormDraft';

interface Props {
  className?: string;
}

export function ShopGoodsCurationCreate({ className }: Props) {
  const { step, setStep } = useShopEditContext();
  const { service } = useDeviceInfo();
  const { data: userInfo } = useQuery(curatorQueries.profile.userInfo);
  const curatorId = userInfo?.snsTitle ?? '';

  const goToSelectGoods = () => {
    if (service === SERVICE.MUSINSA && curatorId) {
      amplitudeSendEvent({
        action_name: 'click',
        button_name: 'goods_add_try',
        component_name: 'button',
        event_name: 'click_button',
        from: getScreenName(),
        id: curatorId,
        section_name: 'curator_curation_create',
        type: 'goods',
      });
    }
    setStep('step2');
  };

  const initialValues = useMemo<ShopCurationEditFormData>(
    () => ({
      title: '',
      description: '',
      selectedProducts: [] as CurationFormGoodsItem[],
    }),
    [],
  );

  const form = useForm<ShopCurationEditFormData>({
    resolver: zodResolver(editCurationFormSchema),
    defaultValues: initialValues,
  });
  useCurationFormDraft(getCurationFormDraftKey('goods'), form, initialValues);

  return (
    <FormProvider {...form}>
      <div
        className={twMerge(
          'flex w-full grow flex-col',
          className,
          step === 'step1' ? 'bg-neutral-color-white' : '',
        )}
      >
        <CreateGoodsCurationForm
          className={step !== 'step1' ? 'hidden' : ''}
          onClickSelectGoods={goToSelectGoods}
        />
        {step === 'step2' && (
          <SelectGoodsSelectionStep
            defaultCheckedProducts={form.getValues('selectedProducts')}
            onSubmit={() => setStep('step1')}
          />
        )}
      </div>
    </FormProvider>
  );
}
